import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';

export default function BlogPage() {
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const heroAnimation = useScrollAnimation({ threshold: 0.2 });
  const articlesAnimation = useScrollAnimation({ threshold: 0.2 });

  const articles = [
    {
      id: 1,
      slug: 'revaloriza-tu-inmueble',
      title: 'Revaloriza tu Inmueble: La Inversión que Siempre Gana',
      subtitle: 'Por qué remodelar tu hogar es la mejor decisión financiera',
      excerpt: 'Descubre cómo una remodelación bien planificada puede aumentar significativamente el valor de tu propiedad y convertirse en la mejor inversión a largo plazo.',
      image: 'https://public.readdy.ai/ai/img_res/76a0b346c8469b5bd9a30f8689d5a83f.jpg',
      date: '15 Enero 2024',
      readTime: '5 min',
      category: 'Inversión Inmobiliaria'
    },
    {
      id: 2,
      slug: 'olvidate-del-estres',
      title: 'Olvídate del Estrés: Nosotros Coordinamos Todo por Ti',
      subtitle: 'Cómo HogarSoluciones simplifica tu proyecto de remodelación',
      excerpt: 'El dolor de cabeza de buscar técnicos confiables terminó. Te mostramos cómo coordinamos equipos completos para que tú solo disfrutes del resultado.',
      image: 'https://readdy.ai/api/search-image?query=happy%20relaxed%20homeowner%20sitting%20comfortably%20while%20professional%20construction%20team%20works%20in%20background%20stress%20free%20home%20renovation%20experience%20ecuadorian%20setting%20natural%20lighting%20professional%20photography&width=800&height=500&seq=blog-estres-001&orientation=landscape',
      date: '12 Enero 2024',
      readTime: '4 min',
      category: 'Gestión de Proyectos'
    },
    {
      id: 3,
      slug: 'planifica-inteligentemente',
      title: 'Planifica Inteligentemente: De la Idea a la Realidad',
      subtitle: 'Guía completa para tu proyecto de remodelación sin sorpresas',
      excerpt: 'Desde presupuestos accesibles hasta el respaldo de profesionales. Todo lo que necesitas saber para convertir tu idea en realidad sin contratiempos.',
      image: 'https://readdy.ai/api/search-image?query=architect%20and%20homeowner%20reviewing%20detailed%20renovation%20blueprints%20and%20plans%20on%20table%20with%20laptop%20calculator%20and%20design%20materials%20professional%20planning%20meeting%20ecuadorian%20office%20setting%20bright%20natural%20lighting&width=800&height=500&seq=blog-planifica-001&orientation=landscape',
      date: '10 Enero 2024',
      readTime: '6 min',
      category: 'Planificación'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed w-full z-50 transition-all duration-500 ${isScrolled ? 'bg-white shadow-md' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <Link to="/" className="flex items-center space-x-3 cursor-pointer">
              {/* Logo actualizado */}
              <img 
                src="https://static.readdy.ai/image/e5ec1a966ead4ea4405a383d967bb80c/5b74a69037f50dd7137b29dc6f7b5981.png"
                alt="HogarSoluciones Ecuador Logo"
                className="h-16 w-auto"
              />
            </Link>
            
            <div className="hidden md:flex items-center space-x-8">
              <Link to="/" className={`text-lg font-medium transition-colors cursor-pointer ${isScrolled ? 'text-gray-700 hover:text-teal-600' : 'text-white hover:text-teal-200'}`}>
                Inicio
              </Link>
              <Link to="/servicios" className={`text-lg font-medium transition-colors cursor-pointer ${isScrolled ? 'text-gray-700 hover:text-teal-600' : 'text-white hover:text-teal-200'}`}>
                Servicios
              </Link>
              <Link to="/blog" className={`text-lg font-medium transition-colors cursor-pointer ${isScrolled ? 'text-teal-600' : 'text-white'}`}>
                Blog
              </Link>
              <Link to="/contacto" className={`text-lg font-medium transition-colors cursor-pointer ${isScrolled ? 'text-gray-700 hover:text-teal-600' : 'text-white hover:text-teal-200'}`}>
                Únete como Técnico
              </Link>
              <a href="/#solicitar" className="bg-teal-600 text-white px-6 py-3 rounded-lg text-base font-semibold hover:bg-teal-700 whitespace-nowrap cursor-pointer">
                Solicitar Servicio
              </a>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section ref={heroAnimation.elementRef} className="relative pt-32 pb-20 bg-gradient-to-br from-teal-600 via-teal-500 to-cyan-500 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/10 to-black/20"></div>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-white rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-white rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
        </div>
        
        <div className={`relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center transition-all duration-1000 ${
          heroAnimation.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
        }`}>
          <div className="inline-flex items-center justify-center w-20 h-20 bg-white rounded-full mb-6 animate-bounce">
            <i className="ri-article-line text-4xl text-teal-600"></i>
          </div>
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight">
            Blog HogarSoluciones
          </h1>
          <p className="text-xl md:text-2xl text-white/95 mb-4 max-w-4xl mx-auto leading-relaxed">
            Consejos, Ideas y Guías para Revalorizar tu Inmueble
          </p>
          <p className="text-lg text-white/90 max-w-3xl mx-auto leading-relaxed">
            Descubre cómo transformar tu hogar, oficina o local comercial con inversiones inteligentes que aumentan el valor de tu propiedad
          </p>
        </div>
      </section>

      {/* Articles Grid */}
      <section ref={articlesAnimation.elementRef} className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {articles.map((article, index) => (
              <div
                key={article.id}
                className={`bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-2xl hover:scale-105 transition-all duration-500 cursor-pointer ${
                  articlesAnimation.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                }`}
                style={{ transitionDelay: `${index * 0.1}s` }}
              >
                <Link to={`/blog/${article.slug}`}>
                  <div className="relative h-64 overflow-hidden">
                    <img
                      src={article.image}
                      alt={article.title}
                      className="w-full h-full object-cover object-center hover:scale-110 transition-transform duration-700"
                    />
                    <div className="absolute top-4 left-4">
                      <span className="bg-teal-600 text-white px-4 py-2 rounded-full text-sm font-semibold">
                        {article.category}
                      </span>
                    </div>
                  </div>
                  
                  <div className="p-6">
                    <div className="flex items-center space-x-4 text-sm text-gray-500 mb-3">
                      <span className="flex items-center">
                        <i className="ri-calendar-line mr-1"></i>
                        {article.date}
                      </span>
                      <span className="flex items-center">
                        <i className="ri-time-line mr-1"></i>
                        {article.readTime}
                      </span>
                    </div>
                    
                    <h3 className="text-2xl font-bold text-gray-900 mb-2 hover:text-teal-600 transition-colors">
                      {article.title}
                    </h3>
                    
                    <p className="text-base font-semibold text-teal-600 mb-3">
                      {article.subtitle}
                    </p>
                    
                    <p className="text-gray-600 leading-relaxed mb-4">
                      {article.excerpt}
                    </p>
                    
                    <div className="flex items-center text-teal-600 font-semibold hover:text-teal-700 transition-colors">
                      Leer más
                      <i className="ri-arrow-right-line ml-2"></i>
                    </div>
                  </div>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-teal-600 to-cyan-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            ¿Listo para Transformar tu Inmueble?
          </h2>
          <p className="text-xl text-white/95 mb-8 max-w-3xl mx-auto">
            Habla con nuestros asesores y descubre cómo podemos ayudarte a revalorizar tu propiedad
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="https://wa.me/593978966415?text=Hola%2C%20quiero%20información%20sobre%20remodelaciones"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center bg-green-500 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-green-600 hover:scale-105 transition-all duration-300 whitespace-nowrap cursor-pointer shadow-lg"
            >
              <i className="ri-whatsapp-line text-2xl mr-3"></i>
              Contactar por WhatsApp
            </a>
            <Link
              to="/#solicitar"
              className="inline-flex items-center bg-white text-teal-600 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 hover:scale-105 transition-all duration-300 whitespace-nowrap cursor-pointer shadow-lg"
            >
              <i className="ri-file-list-line text-2xl mr-3"></i>
              Solicitar Presupuesto
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center space-x-3 mb-4">
                {/* Logo en Footer actualizado */}
                <img 
                  src="https://static.readdy.ai/image/e5ec1a966ead4ea4405a383d967bb80c/5b74a69037f50dd7137b29dc6f7b5981.png"
                  alt="HogarSoluciones Ecuador Logo"
                  className="h-12 w-auto"
                />
              </div>
              <p className="text-gray-400 mb-6 leading-relaxed">
                Técnicos especializados a su servicio, soluciones a su alcance. Conectando hogares, oficinas y locales comerciales con profesionales en todo Ecuador.
              </p>
              
              <div className="space-y-4">
                <h4 className="text-lg font-semibold mb-4">Síguenos en Redes Sociales</h4>
                <div className="flex flex-wrap gap-4">
                  <a 
                    href="https://www.facebook.com/share/1DAL4GHqeD/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="group"
                  >
                    <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center hover:bg-blue-700 hover:scale-110 transition-all duration-300 cursor-pointer shadow-lg">
                      <i className="ri-facebook-fill text-3xl text-white"></i>
                    </div>
                    <p className="text-xs text-center mt-2 text-gray-400 group-hover:text-white transition-colors">Facebook</p>
                  </a>
                  
                  <a 
                    href="https://www.instagram.com/hogarsolucionesecuador/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="group"
                  >
                    <div className="w-16 h-16 bg-gradient-to-br from-purple-600 via-pink-600 to-orange-500 rounded-full flex items-center justify-center hover:scale-110 transition-all duration-300 cursor-pointer shadow-lg">
                      <i className="ri-instagram-fill text-3xl text-white"></i>
                    </div>
                    <p className="text-xs text-center mt-2 text-gray-400 group-hover:text-white transition-colors">Instagram</p>
                  </a>
                  
                  <a 
                    href="https://wa.me/593978966415" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="group"
                  >
                    <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center hover:bg-green-600 hover:scale-110 transition-all duration-300 cursor-pointer shadow-lg border-2 border-teal-400">
                      <i className="ri-whatsapp-fill text-3xl text-white"></i>
                    </div>
                    <p className="text-xs text-center mt-2 text-gray-400 group-hover:text-white transition-colors">WhatsApp</p>
                  </a>
                  
                  <a 
                    href="https://www.tiktok.com/@hogarsolucionesecuador" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="group"
                  >
                    <div className="w-16 h-16 bg-black rounded-full flex items-center justify-center hover:bg-gray-800 hover:scale-110 transition-all duration-300 cursor-pointer shadow-lg border-2 border-teal-400">
                      <i className="ri-tiktok-fill text-3xl text-white"></i>
                    </div>
                    <p className="text-xs text-center mt-2 text-gray-400 group-hover:text-white transition-colors">TikTok</p>
                  </a>
                  
                  <a 
                    href="mailto:info@hogarsolucionesecuador.com"
                    className="group"
                  >
                    <div className="w-16 h-16 bg-red-600 rounded-full flex items-center justify-center hover:bg-red-700 hover:scale-110 transition-all duration-300 cursor-pointer shadow-lg">
                      <i className="ri-mail-fill text-3xl text-white"></i>
                    </div>
                    <p className="text-xs text-center mt-2 text-gray-400 group-hover:text-white transition-colors">Email</p>
                  </a>
                </div>
              </div>
            </div>
            
            <div>
              <h4 className="text-lg font-semibold mb-4">Servicios</h4>
              <ul className="space-y-2">
                <li><Link to="/servicios" className="text-gray-400 hover:text-white cursor-pointer">Albañilería</Link></li>
                <li><Link to="/servicios" className="text-gray-400 hover:text-white cursor-pointer">Plomería</Link></li>
                <li><Link to="/servicios" className="text-gray-400 hover:text-white cursor-pointer">Cerrajería</Link></li>
                <li><Link to="/servicios" className="text-gray-400 hover:text-white cursor-pointer">Pintura</Link></li>
                <li><Link to="/servicios" className="text-gray-400 hover:text-white cursor-pointer">Electricidad</Link></li>
                <li><Link to="/servicios" className="text-gray-400 hover:text-white cursor-pointer">Refrigeración y A/C</Link></li>
                <li><Link to="/servicios" className="text-gray-400 hover:text-white cursor-pointer">Jardinería</Link></li>
              </ul>
            </div>
            
            <div>
              <h4 className="text-lg font-semibold mb-4">Enlaces</h4>
              <ul className="space-y-2">
                <li><Link to="/" className="text-gray-400 hover:text-white cursor-pointer">Inicio</Link></li>
                <li><Link to="/servicios" className="text-gray-400 hover:text-white cursor-pointer">Servicios</Link></li>
                <li><Link to="/blog" className="text-gray-400 hover:text-white cursor-pointer">Blog</Link></li>
                <li><Link to="/contacto" className="text-gray-400 hover:text-white cursor-pointer">Únete como Técnico</Link></li>
                <li><a href="/#solicitar" className="text-gray-400 hover:text-white cursor-pointer">Solicitar Servicio</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="text-lg font-semibold mb-4">Contacto</h4>
              <ul className="space-y-2">
                <li className="text-gray-400">📧 info@hogarsolucionesecuador.com</li>
                <li>
                  <a href="tel:+593978966415" className="text-gray-400 hover:text-white cursor-pointer">
                    📱 +593 978 966 415 - GERMÁN
                  </a>
                </li>
                <li>
                  <a href="tel:+584248082841" className="text-gray-400 hover:text-white cursor-pointer">
                    📱 +58 424 808 2841 - MILVA
                  </a>
                </li>
                <li className="text-gray-400">📍 Otavalo, Ecuador</li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-800 mt-12 pt-8 flex flex-col md:flex-row justify-between items-center">
            <p className="text-gray-400 text-sm">
              © 2024 HogarSoluciones Ecuador. Todos los derechos reservados.
            </p>
            <a href="https://readdy.ai/?origin=logo" className="text-gray-400 hover:text-white text-sm cursor-pointer">
              Powered by Readdy
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}